import { NavLink } from "react-router-dom";
import type { Role } from "../../api/types";
import { can } from "../../auth/rbac";
import { dataModeBadge } from "../../lib/env";
import { cn } from "../../lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: string;
  group?: string;
  visible: (role: Role | undefined) => boolean;
}

const navItems: NavItem[] = [
  { to: "/", label: "Gösterge Paneli", icon: "▦", visible: () => true },
  { to: "/devices", label: "Cihazlar", icon: "🖥", visible: () => true },
  { to: "/patches", label: "Patch Uyumluluğu", icon: "⟳", visible: () => true },
  { to: "/alerts", label: "Uyarılar", icon: "⚠", visible: () => true },
  {
    to: "/alert-rules",
    label: "Uyarı Kuralları",
    icon: "⚙",
    visible: (role) => can(role, "manageAlertRules"),
  },
  { to: "/snmp", label: "SNMP Hedefleri", icon: "⇄", visible: () => true },
  {
    to: "/settings/install",
    label: "Agent Kurulumu",
    icon: "⬇",
    group: "Ayarlar",
    visible: () => true,
  },
  {
    to: "/users",
    label: "Kullanıcılar",
    icon: "👤",
    group: "Ayarlar",
    visible: (role) => can(role, "manageUsers"),
  },
  {
    to: "/settings/enrollment",
    label: "Enrollment",
    icon: "🔑",
    group: "Ayarlar",
    visible: (role) => can(role, "manageEnrollment"),
  },
];

const groupOrder = ["Ayarlar"];

interface SidebarProps {
  role: Role | undefined;
  open: boolean;
  onNavigate: () => void;
}

export function Sidebar({ role, open, onNavigate }: SidebarProps) {
  return (
    <aside
      aria-label="Ana gezinme"
      className={cn(
        "bg-card border-border fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r transition-transform duration-200 lg:static lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full",
      )}
    >
      <div className="border-border flex h-16 items-center gap-2 border-b px-5">
        <span aria-hidden="true" className="bg-primary text-primary-foreground flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold">
          N
        </span>
        <div>
          <p className="text-sm leading-tight font-semibold">İzleme Platformu</p>
          <p className="text-muted-foreground text-xs">Admin Panel</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3">
        <ul className="flex flex-col gap-1">
          {navItems
            .filter((item) => item.visible(role) && !item.group)
            .map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )
                  }
                >
                  <span aria-hidden="true" className="w-5 text-center">
                    {item.icon}
                  </span>
                  {item.label}
                </NavLink>
              </li>
            ))}
          {groupOrder.map((group) => {
            const groupItems = navItems.filter(
              (item) => item.group === group && item.visible(role),
            );
            if (groupItems.length === 0) return null;
            return (
              <li key={group} className="mt-4">
                <p
                  id={`nav-group-${group}`}
                  className="text-muted-foreground px-3 pb-1 text-xs font-semibold tracking-wide uppercase"
                >
                  {group}
                </p>
                <ul className="flex flex-col gap-1" aria-labelledby={`nav-group-${group}`}>
                  {groupItems.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none",
                            isActive
                              ? "bg-primary/10 text-primary"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground",
                          )
                        }
                      >
                        <span aria-hidden="true" className="w-5 text-center">
                          {item.icon}
                        </span>
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      </nav>
      <div className="border-border text-muted-foreground border-t px-5 py-3 text-xs">
        {dataModeBadge()}
      </div>
    </aside>
  );
}